import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";

/**
 * CheckoutSuccessPage — Batch 3.5 (iter79)
 */
export default function CheckoutSuccessPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling", payment_status: "pending" });
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) return;
    const poll = async (attempt) => {
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        setState(data);
        if (data.payment_status === "paid" || data.status === "COMPLETED") return;
        if (data.status === "EXPIRED" || attempt >= 8) { setState((s) => ({ ...s, status: "TIMEOUT" })); return; }
        setTimeout(() => { setAttempts(attempt + 1); poll(attempt + 1); }, Math.min(2000 * Math.pow(1.3, attempt), 8000));
      } catch (e) {
        if (attempt >= 5) { setState({ status: "ERROR", payment_status: "error" }); return; }
        setTimeout(() => poll(attempt + 1), 3000);
      }
    };
    poll(0);
  }, [sessionId]);

  if (!sessionId) return <div data-testid="checkout-success" className="p-8 text-center text-sm text-[#999]">Keine Session-ID.</div>;

  const done = state.payment_status === "paid" || state.status === "COMPLETED";
  const failed = state.status === "EXPIRED" || state.status === "TIMEOUT" || state.status === "ERROR";

  return (
    <div data-testid="checkout-success" className="flex flex-col items-center justify-center min-h-[70vh] px-4 text-center">
      {!done && !failed && (
        <>
          <Loader2 size={40} className="animate-spin text-[#0A0A0A]" />
          <p className="mt-4 text-sm font-semibold text-[#0A0A0A]">{t("checkout_success.confirming")}</p>
          <p className="text-xs text-[#999]">{t("checkout_success.wait_hint")}</p>
        </>
      )}
      {done && (
        <>
          <CheckCircle size={48} className="text-green-500" />
          <p className="mt-4 text-lg font-bold text-[#0A0A0A]">
            {state.kind === "ORDER" || state.kind === "CHECKOUT_SESSION" ? t("checkout_success.order_paid") : t("checkout_success.purchase_ok")}
          </p>
          <p className="text-sm text-[#666]">
            {state.kind === "ORDER" || state.kind === "CHECKOUT_SESSION"
              ? t("checkout_success.order_body")
              : t("checkout_success.access_body")}
          </p>
          <button data-testid="checkout-success-cta"
            onClick={() => navigate((state.kind === "ORDER" || state.kind === "CHECKOUT_SESSION") ? "/profile/purchases" : "/?tab=exklusiv")}
            className="mt-6 rounded-full h-11 px-6 bg-[#0A0A0A] text-white text-sm font-medium">
            {state.kind === "ORDER" || state.kind === "CHECKOUT_SESSION" ? t("checkout_success.cta_orders") : t("checkout_success.cta_exclusive")}
          </button>
        </>
      )}
      {failed && (
        <>
          <XCircle size={48} className="text-red-500" />
          <p className="mt-4 text-lg font-bold text-[#0A0A0A]">{t("checkout_success.failed_title")}</p>
          <p className="text-sm text-[#666]">{t("checkout_success.failed_body")}</p>
          <button onClick={() => navigate(-1)} className="mt-6 h-11 px-6 border border-[#E5E5E5] text-sm">{t("common.back")}</button>
        </>
      )}
    </div>
  );
}
